import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, fmtPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProductForm, EMPTY_PRODUCT } from "../components/admin/ProductForm";
import { OrdersPanel } from "../components/admin/OrdersPanel";
import { EarlyAccessPanel } from "../components/admin/EarlyAccessPanel";

const TABS = [["pieces", "Pieces"], ["orders", "Orders"], ["early-access", "Early Access"]];

const ProductRow = ({ product, onEdit, onDelete }) => {
  const stock = Object.values(product.inventory || {}).reduce((s, v) => s + v, 0);
  return (
    <div data-testid="admin-product-row" className="bg-[#EDEEF1] p-5 flex gap-4">
      <img src={product.image} alt={product.name} className="w-20 h-28 object-cover bg-[#E2E4E8]" />
      <div className="flex-1 flex flex-col">
        <h3 className="font-serif-display text-xl leading-tight">{product.name}</h3>
        <p className="label text-[#8A8F99] mt-1">{product.category}{product.featured ? " · Featured" : ""}</p>
        <p className="text-sm mt-2">{fmtPrice(product.price)}</p>
        {product.inventory && Object.keys(product.inventory).length > 0 && (
          <p className="label text-[#8A8F99] mt-1" data-testid="admin-stock">{stock} in stock</p>
        )}
        <div className="mt-auto flex gap-3 pt-3">
          <button onClick={() => onEdit(product)} data-testid="edit-product" className="label flex items-center gap-1 link-underline"><Pencil className="w-3 h-3" /> Edit</button>
          <button onClick={() => onDelete(product.id)} data-testid="delete-product" className="label flex items-center gap-1 text-red-700 link-underline"><Trash2 className="w-3 h-3" /> Delete</button>
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const { logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("pieces");
  const [editing, setEditing] = useState(null); // null | {form, id?}

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = (p) => setEditing({ id: p.id, form: { name: p.name, category: p.category, price: p.price, description: p.description, image: p.image, images: p.images || [], featured: p.featured, sizes: p.sizes, inventory: p.inventory || {} } });

  const del = async (id) => {
    if (!window.confirm("Remove this piece permanently?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Piece removed");
    load();
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-28 pb-24 px-4 sm:px-8 md:px-12">
      <div className="w-full mx-auto">
        <div className="flex items-end justify-between border-b border-[#0B0E14] pb-8 mb-10">
          <div>
            <p className="label text-[#8A8F99] mb-3">Funky Apparel · Studio</p>
            <h1 className="font-serif-display text-4xl sm:text-6xl tracking-tight leading-none">Manage Collection</h1>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="add-product-button" onClick={() => setEditing({ form: { ...EMPTY_PRODUCT } })} className="bg-[#0B0E14] text-[#EDEEF1] px-6 h-12 label flex items-center gap-2 hover:bg-[#1B2233] transition-colors">
              <Plus className="w-4 h-4" /> New Piece
            </button>
            <button onClick={logout} data-testid="logout-button" className="label flex items-center gap-2 link-underline"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>

        <div className="flex items-center gap-6 border-b border-[#D4D7DD] mb-10">
          {TABS.map(([key, labelText]) => (
            <button
              key={key} onClick={() => setTab(key)}
              data-testid={`tab-${key}`}
              className={`label pb-3 -mb-px border-b-2 transition-colors ${tab === key ? "border-[#0B0E14] text-[#0B0E14]" : "border-transparent text-[#8A8F99] hover:text-[#0B0E14]"}`}
            >
              {labelText}
            </button>
          ))}
        </div>

        {tab === "pieces" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D4D7DD] border border-[#D4D7DD]">
            {products.map((p) => <ProductRow key={p.id} product={p} onEdit={openEdit} onDelete={del} />)}
          </div>
        )}
        {tab === "orders" && <OrdersPanel />}
        {tab === "early-access" && <EarlyAccessPanel />}
      </div>

      <ProductForm editing={editing} setEditing={setEditing} onSaved={load} />
    </div>
  );
}
